class AuthService {
  constructor() {
    this.currentUser = null;
    this.backendURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    this.pollInterval = null;
  }

  async initialize() {
    // Check if user is already logged in
    const storedUser = localStorage.getItem('sarvik_user');
    const storedToken = localStorage.getItem('sarvik_token');
    
    if (storedUser && storedToken) {
      // Verify token with backend
      try {
        const response = await fetch(`${this.backendURL}/api/auth/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: storedToken })
        });
        
        if (response.ok) {
          this.currentUser = JSON.parse(storedUser);
          return true;
        }
      } catch (error) {
        console.error('Token verification failed:', error);
      }
      
      // Token invalid, clear storage
      this.clearAuth();
    }
    
    return true;
  }

  async signInWithGoogle() {
    try {
      // Get OAuth URL from backend
      const response = await fetch(`${this.backendURL}/api/auth/google/login`);
      const data = await response.json();
      
      // Check if running in Electron
      const isElectron = window.electronAPI !== undefined;
      
      if (isElectron) {
        // Open in external browser for Electron
        if (window.electronAPI.openOAuthURL) {
          window.electronAPI.openOAuthURL(data.auth_url);
        } else {
          window.open(data.auth_url, '_blank');
        }
        
        // Start polling for auth completion
        return this.startPolling();
      } else {
        // For regular browser, redirect
        window.location.href = data.auth_url;
      }
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      throw error;
    }
  }

  async startPolling() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 120; // 2 minutes total
      
      // ⚠️ POLLING OPTIMIZATION: Implement exponential backoff while maintaining speed
      let currentInterval = 1000; // Start with 1 second (fast for immediate response)
      const maxInterval = 5000; // Max 5 seconds to maintain responsiveness
      const backoffFactor = 1.1; // Gentle backoff to balance speed and efficiency
      
      // Clear any existing interval
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        clearTimeout(this.pollInterval);
      }
      
      const poll = async () => {
        attempts++;
        
        try {
          // Poll backend for auth status
          const response = await fetch(`${this.backendURL}/api/auth/poll`);
          const data = await response.json();
          
          if (data.authenticated !== false && data.user) {
            // ✅ Auth successful! Clear polling immediately
            if (this.pollInterval) {
              clearInterval(this.pollInterval);
              clearTimeout(this.pollInterval);
              this.pollInterval = null;
            }
            
            // Store user data
            this.currentUser = data.user;
            localStorage.setItem('sarvik_user', JSON.stringify(data.user));
            localStorage.setItem('sarvik_token', data.access_token);
            
            // Notify app
            window.dispatchEvent(new CustomEvent('authStateChanged', {
              detail: { user: this.currentUser }
            }));
            
            console.log(`✅ Auth completed in ${attempts} attempts with optimized polling`);
            resolve(this.currentUser);
            return;
          }
        } catch (error) {
          console.log(`Polling for auth... (attempt ${attempts}/${maxAttempts})`);
        }
        
        if (attempts >= maxAttempts) {
          if (this.pollInterval) {
            clearInterval(this.pollInterval);
            clearTimeout(this.pollInterval);
            this.pollInterval = null;
          }
          reject(new Error('Authentication timeout'));
          return;
        }
        
        // ⚠️ POLLING OPTIMIZATION: Exponential backoff after initial fast attempts
        if (attempts > 10) { // After 10 seconds, start backing off
          currentInterval = Math.min(currentInterval * backoffFactor, maxInterval);
        }
        
        // Schedule next poll with updated interval
        this.pollInterval = setTimeout(poll, currentInterval);
      };
      
      // Start immediate polling
      poll();
    });
  }

  async signOut() {
    try {
      // Call logout endpoint
      await fetch(`${this.backendURL}/api/auth/logout`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear local data
    this.clearAuth();
    
    // Notify app
    window.dispatchEvent(new CustomEvent('authStateChanged', {
      detail: { user: null }
    }));
  }

  clearAuth() {
    this.currentUser = null;
    localStorage.removeItem('sarvik_user');
    localStorage.removeItem('sarvik_token');
    
    // ⚠️ POLLING OPTIMIZATION: Clear both interval and timeout
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      clearTimeout(this.pollInterval); // Handle both setInterval and setTimeout
      this.pollInterval = null;
    }
  }

  getCurrentUser() {
    return this.currentUser;
  }

  getToken() {
    return localStorage.getItem('sarvik_token');
  }

  isAuthenticated() {
    return !!this.currentUser && !!this.getToken();
  }
}

export const authService = new AuthService();
